// ============================================
// Quality Platform Hub
// Central dashboard for all quality modules.
// Shows readiness gauge, module cards, test
// history, and recent activity.
// /quality
// ============================================

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { T, F, modules, scoreColor } from './tokens';
import { ModuleShell } from './ModuleShell';
import { PassGauge } from './PassGauge';
import { getTestRuns } from '../../services/firestore/pjeService';
import type { TestRun } from '../../types/patientJourney';

// ── Demo data (fallback when Firestore is empty) ──

interface HistoryRun {
  type: 'Tournament' | 'Single';
  date: string;
  version: string;
  avg: number;
  results: { name: string; score: number }[];
}

const MODULE_CARDS = [
  { key: 'plate' as const, desc: 'Visual nurse persona dressing', route: '/fashion-plate' },
  { key: 'twin' as const, desc: 'AI vs AI automated testing', route: '/twin-fighter' },
  { key: 'chat' as const, desc: 'Patient chat demo \u2014 conversion spine', route: '/patient-chat' },
  { key: 'doc' as const, desc: 'Doctor calibration & training', route: '/doc-module' },
  { key: 'teach' as const, desc: 'Capture expertise → prompt rules', route: '/teach' },
  { key: 'nurse' as const, desc: 'Manual patient testing', route: '/nurse-test' },
  { key: 'viewer' as const, desc: 'Watch & debug conversations', route: '/twin-test' },
];

const RECENT_ACTIVITY: { abbr: string; color: string; text: string; detail: string; time: string }[] = [];

// ── Component ──

export function QualityHub() {
  const navigate = useNavigate();
  const [showHistory, setShowHistory] = useState(false);
  const [historyData, setHistoryData] = useState<HistoryRun[]>([]);
  const [currentScore, setCurrentScore] = useState(0);
  const [passingCount, setPassingCount] = useState(0);

  // Try loading real data from Firestore
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const runs = await getTestRuns({ suiteId: 'twin-fighter', limit: 20 });
        if (cancelled || runs.length === 0) return;

        // Convert real test runs to HistoryRun format
        const grouped = groupRunsBySuite(runs);
        if (grouped.length > 0) {
          setHistoryData(grouped);
          const latest = grouped[0];
          setCurrentScore(latest.avg);
          setPassingCount(latest.results.filter(r => r.score >= 70).length);
        }
      } catch {
        // Firestore may not be available — use demo data
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const latestRun = historyData[0];
  const previousRun = historyData.length > 1 ? historyData[1] : null;
  const delta = previousRun ? currentScore - previousRun.avg : 0;

  return (
    <ModuleShell>
      {showHistory ? (
        <HistoryView
          data={historyData}
          currentScore={currentScore}
          previousScore={previousRun?.avg ?? 0}
          delta={delta}
          onBack={() => setShowHistory(false)}
        />
      ) : (
        <>
          {/* Header */}
          <div style={{
            padding: '20px 20px 12px',
            background: 'linear-gradient(180deg, rgba(225,29,72,0.05) 0%, transparent 100%)',
          }}>
            <div style={{ fontFamily: F.body, fontSize: T.fontSize.xl, fontWeight: 700, color: T.text, marginBottom: 2 }}>
              Hemo<span style={{ color: T.accent }}>Logix</span> Quality
            </div>
            <div style={{ fontSize: T.fontSize.sm, color: T.textMuted, fontFamily: F.body }}>
              5 modules · 8 personas · 40+ validation checks
            </div>
          </div>

          <div style={{ padding: '0 20px' }}>
            {/* Readiness Score — tap for history */}
            <div
              onClick={() => setShowHistory(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: 16,
                padding: '16px 20px', background: T.bgCard,
                borderRadius: T.radius.lg, border: `1px solid ${T.border}`,
                boxShadow: T.shadowMd, marginBottom: 20, cursor: 'pointer',
              }}
            >
              <PassGauge score={currentScore} size={80} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: T.fontSize.sm, fontWeight: 600, color: T.text, fontFamily: F.body }}>
                  Platform Readiness
                </div>
                <div style={{ fontSize: T.fontSize.xs, color: T.textMuted, fontFamily: F.body, marginTop: 2 }}>
                  {passingCount}/8 personas passing
                </div>
                <div style={{ fontSize: 10, color: T.textGhost, fontFamily: F.mono, marginTop: 4 }}>
                  Last tournament: {latestRun?.date ?? 'never'}
                </div>
              </div>
              <span style={{ fontSize: 14, color: T.textGhost }}>&rsaquo;</span>
            </div>

            {/* Module Cards */}
            {MODULE_CARDS.map(({ key, desc, route }) => {
              const m = modules[key];
              return (
                <div
                  key={key}
                  onClick={() => navigate(route)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '14px 16px', background: T.bgCard,
                    borderRadius: T.radius.md,
                    border: `1px solid ${T.border}`,
                    borderLeft: `3px solid ${m.color}`,
                    boxShadow: T.shadowSm, marginBottom: 10,
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: T.fontSize.base, fontWeight: 600, color: T.text, fontFamily: F.body }}>
                      {m.name}
                    </div>
                    <div style={{ fontSize: T.fontSize.sm, color: T.textMuted, fontFamily: F.body }}>{desc}</div>
                  </div>
                  <span style={{ fontSize: 16, color: m.color }}>&rsaquo;</span>
                </div>
              );
            })}

            {/* Quick Actions */}
            <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
              <button
                onClick={() => navigate('/twin-fighter')}
                style={{
                  flex: 1, height: 48, borderRadius: T.radius.md,
                  border: 'none', cursor: 'pointer',
                  background: `linear-gradient(135deg, ${T.accent}, ${T.cherry})`,
                  color: '#fff', fontFamily: F.body, fontSize: T.fontSize.sm,
                  fontWeight: 600, boxShadow: T.shadowAccent,
                }}
              >
                Run Full Tournament
              </button>
              <button
                onClick={() => setShowHistory(true)}
                style={{
                  flex: 1, height: 48, borderRadius: T.radius.md,
                  cursor: 'pointer', background: T.bgCard,
                  border: `1px solid ${T.borderStrong}`, color: T.text,
                  fontFamily: F.body, fontSize: T.fontSize.sm, fontWeight: 500,
                  boxShadow: T.shadowSm,
                }}
              >
                Prompt History
              </button>
            </div>

            {/* Recent Activity */}
            <div style={{ marginTop: 20, marginBottom: 20 }}>
              <div style={{
                fontSize: 10, fontFamily: F.mono, color: T.accent,
                letterSpacing: 2, fontWeight: 600, marginBottom: 10,
              }}>
                RECENT ACTIVITY
              </div>
              {RECENT_ACTIVITY.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '16px 0' }}>
                  <div style={{ fontSize: T.fontSize.sm, color: T.textMuted, fontFamily: F.body }}>
                    Run your first tournament to see activity here
                  </div>
                </div>
              ) : (
                RECENT_ACTIVITY.map((a, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 0',
                      borderBottom: i < RECENT_ACTIVITY.length - 1 ? `1px solid ${T.border}` : 'none',
                    }}
                  >
                    <div style={{
                      width: 28, height: 28, borderRadius: T.radius.sm,
                      background: `${a.color}10`, border: `1px solid ${a.color}25`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: F.mono, fontSize: 8, fontWeight: 700,
                      color: a.color, letterSpacing: 0.3, flexShrink: 0,
                    }}>
                      {a.abbr}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: T.fontSize.sm, fontWeight: 500, color: T.text, fontFamily: F.body }}>
                        {a.text}
                      </div>
                      <div style={{ fontSize: T.fontSize.xs, color: T.textMuted, fontFamily: F.body }}>
                        {a.detail}
                      </div>
                    </div>
                    <span style={{ fontSize: 10, color: T.textGhost, fontFamily: F.mono }}>{a.time}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </ModuleShell>
  );
}

// ── Test History View ──

function HistoryView({
  data, currentScore, previousScore, delta, onBack,
}: {
  data: HistoryRun[];
  currentScore: number;
  previousScore: number;
  delta: number;
  onBack: () => void;
}) {
  const versionRange = data.length > 1
    ? `${data[data.length - 1].version}–${data[0].version}`
    : data[0]?.version ?? '';

  return (
    <>
      <div style={{
        padding: '16px 20px 12px',
        background: 'linear-gradient(180deg, rgba(225,29,72,0.05) 0%, transparent 100%)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={onBack}
            style={{
              background: 'none', border: 'none', fontSize: 20,
              cursor: 'pointer', padding: 0, color: T.text,
            }}
          >
            &#8592;
          </button>
          <div>
            <div style={{ fontFamily: F.body, fontSize: T.fontSize.md, fontWeight: 700, color: T.text }}>
              Test History
            </div>
            <div style={{ fontSize: T.fontSize.xs, color: T.textMuted, fontFamily: F.body }}>
              {data.length} runs · Prompt {versionRange}
            </div>
          </div>
        </div>
      </div>

      {data.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: T.fontSize.base, fontWeight: 600, color: T.text, fontFamily: F.body, marginBottom: 8 }}>
            No test history yet
          </div>
          <div style={{ fontSize: T.fontSize.sm, color: T.textMuted, fontFamily: F.body }}>
            Run a tournament from Twin Fighter to start tracking scores.
          </div>
        </div>
      )}

      <div style={{ padding: '0 20px' }}>
        {/* Trend Summary */}
        {data.length > 0 && <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          {[
            { label: 'Current', value: `${currentScore}%`, sub: data[0]?.version ?? '', color: T.success },
            { label: 'Previous', value: previousScore > 0 ? `${previousScore}%` : '--', sub: data[1]?.version ?? '', color: T.warning },
            { label: 'Delta', value: delta > 0 ? `+${delta}%` : delta < 0 ? `${delta}%` : '0%', sub: delta > 0 ? 'improving' : delta < 0 ? 'declining' : 'stable', color: delta >= 0 ? T.success : T.danger },
          ].map(s => (
            <div key={s.label} style={{
              flex: 1, padding: '12px 10px', background: T.bgCard,
              borderRadius: T.radius.md, border: `1px solid ${T.border}`,
              boxShadow: T.shadowSm, textAlign: 'center',
            }}>
              <div style={{ fontSize: T.fontSize.lg, fontFamily: F.mono, fontWeight: 700, color: s.color }}>
                {s.value}
              </div>
              <div style={{ fontSize: 10, color: T.textMuted, fontFamily: F.body, marginTop: 2 }}>
                {s.label}
              </div>
              <div style={{ fontSize: 9, color: T.textGhost, fontFamily: F.mono }}>{s.sub}</div>
            </div>
          ))}
        </div>}

        {/* Run List */}
        <div style={{
          fontSize: 10, fontFamily: F.mono, color: T.accent,
          letterSpacing: 2, fontWeight: 600, marginBottom: 10,
        }}>
          ALL RUNS
        </div>
        {data.map((run, ri) => (
          <div key={ri} style={{
            marginBottom: 12, background: T.bgCard,
            borderRadius: T.radius.md, border: `1px solid ${T.border}`,
            boxShadow: T.shadowSm, overflow: 'hidden',
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '12px 14px', borderBottom: `1px solid ${T.border}`,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: T.text, fontFamily: F.body }}>
                    {run.type}
                  </span>
                  <span style={{
                    fontSize: 9, padding: '2px 6px', borderRadius: T.radius.full,
                    background: T.bgInput, color: T.textMuted, fontFamily: F.mono,
                  }}>
                    {run.version}
                  </span>
                </div>
                <div style={{ fontSize: 10, color: T.textGhost, fontFamily: F.body, marginTop: 2 }}>
                  {run.date}
                </div>
              </div>
              <PassGauge score={run.avg} size={44} />
            </div>
            <div style={{ padding: '8px 14px 10px' }}>
              {run.results.map((r, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'space-between', padding: '4px 0',
                }}>
                  <span style={{ fontSize: 12, color: T.textSoft, fontFamily: F.body }}>
                    {r.name}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{
                      width: 48, height: 4, borderRadius: 2,
                      background: T.bgInput, overflow: 'hidden',
                    }}>
                      <div style={{
                        width: `${r.score}%`, height: '100%', borderRadius: 2,
                        background: scoreColor(r.score),
                      }} />
                    </div>
                    <span style={{
                      fontSize: 11, fontFamily: F.mono, fontWeight: 600,
                      color: scoreColor(r.score),
                      width: 28, textAlign: 'right',
                    }}>
                      {r.score}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ── Helpers ──

function groupRunsBySuite(runs: TestRun[]): HistoryRun[] {
  const suiteMap = new Map<string, TestRun[]>();

  for (const run of runs) {
    const key = run.suiteId || run.runId;
    if (!suiteMap.has(key)) suiteMap.set(key, []);
    suiteMap.get(key)!.push(run);
  }

  const result: HistoryRun[] = [];
  for (const [, suiteRuns] of suiteMap) {
    const isTournament = suiteRuns.length > 1;
    const results = suiteRuns.map(r => ({
      name: r.personaId || 'Unknown',
      score: r.passRate ?? Math.round((r.checkResults?.filter(c => c.pass).length ?? 0) / Math.max(r.checkResults?.length ?? 1, 1) * 100),
    })).sort((a, b) => b.score - a.score);

    const avg = results.length > 0
      ? Math.round(results.reduce((s, r) => s + r.score, 0) / results.length)
      : 0;

    const firstRun = suiteRuns[0];
    result.push({
      type: isTournament ? 'Tournament' : 'Single',
      date: firstRun.completedAt
        ? new Date(firstRun.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
        : 'Unknown',
      version: `v${firstRun.suiteVersion ?? '?'}`,
      avg,
      results,
    });
  }

  return result;
}
